'use client';
import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { products, categories, CartItem, Product, sizePriceAdder } from './menuData';
import ProductCard from './ProductCard';
import CartSheet from './CartSheet';
import CustomizeModal from './CustomizeModal';
import Icon from '@/components/ui/AppIcon';

export default function MenuOrderClient() {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [customizing, setCustomizing] = useState<Product | null>(null);
  const [filterVegan, setFilterVegan] = useState(false);
  const [filterDairyFree, setFilterDairyFree] = useState(false);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (activeCategory !== 'all' && p.category !== activeCategory) return false;
      if (filterVegan && !p.tags.includes('vegan')) return false;
      if (filterDairyFree && !p.tags.includes('sütsüz') && !p.tags.includes('vegan')) return false;
      return true;
    });
  }, [activeCategory, filterVegan, filterDairyFree]);

  const cartTotal = cart.reduce((sum, item) => {
    const sizeAdd = sizePriceAdder[item.size] || 0;
    return sum + (item.product.price + sizeAdd) * item.quantity;
  }, 0);

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const starsEarned = Math.floor(cartTotal / 10);

  function addToCart(product: Product, size: 'Tall' | 'Grande' | 'Venti', milk: string, syrup: string, extraShot: boolean) {
    const cartId = `cart-${product.id}-${size}-${milk}-${syrup}-${extraShot ? 'extra' : 'normal'}`;
    setCart((prev) => {
      const existing = prev.find((i) => i.cartId === cartId);
      if (existing) {
        return prev.map((i) => i.cartId === cartId ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { cartId, product, quantity: 1, size, milk, syrup, extraShot }];
    });
    toast.success(`${product.name} sepete eklendi!`, { description: `${size} · ${milk}` });
    setCustomizing(null);
  }

  function quickAdd(product: Product) {
    addToCart(product, 'Grande', 'Tam Yağlı Süt', 'Yok', false);
  }

  function updateQuantity(cartId: string, delta: number) {
    setCart((prev) =>
      prev
        .map((i) => i.cartId === cartId ? { ...i, quantity: i.quantity + delta } : i)
        .filter((i) => i.quantity > 0)
    );
  }

  return (
    <div className="relative">
      {/* Category tabs */}
      <div className="bg-primary-dark sticky top-[56px] z-30">
        <div className="flex gap-2 px-4 py-3 overflow-x-auto scrollbar-hide">
          {categories.map((cat) => (
            <button
              key={`cat-${cat.id}`}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-150 ${
                activeCategory === cat.id
                  ? 'bg-white text-primary-dark' :'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex gap-2 px-4 pb-3">
          <button
            onClick={() => setFilterVegan(!filterVegan)}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all ${
              filterVegan ? 'bg-green-400 text-white' : 'bg-white/10 text-white/70'
            }`}
          >
            <span>🌱</span> Vegan
          </button>
          <button
            onClick={() => setFilterDairyFree(!filterDairyFree)}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all ${
              filterDairyFree ? 'bg-blue-400 text-white' : 'bg-white/10 text-white/70'
            }`}
          >
            <span>🥛</span> Sütsüz
          </button>
          <span className="ml-auto text-white/50 text-xs self-center">{filtered.length} ürün</span>
        </div>
      </div>

      {/* Product grid */}
      <div className="px-4 py-4">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Icon name="ShoppingBagIcon" size={48} className="text-muted-foreground mb-3" />
            <p className="text-foreground font-semibold mb-1">Bu filtrede ürün bulunamadı</p>
            <p className="text-muted-foreground text-sm">Farklı bir kategori veya filtre deneyin</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filtered.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onQuickAdd={() => quickAdd(product)}
                onCustomize={() => setCustomizing(product)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Cart FAB */}
      {cartCount > 0 && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 w-full max-w-app px-4 z-40">
          <button
            onClick={() => setCartOpen(true)}
            className="w-full bg-primary text-white font-bold py-4 rounded-2xl flex items-center justify-between px-5 card-shadow-lg active:scale-[0.98] transition-all duration-150"
          >
            <span className="bg-white/20 text-white text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center">
              {cartCount}
            </span>
            <span className="text-base">Sepeti Görüntüle</span>
            <span className="text-base font-bold">₺{cartTotal}</span>
          </button>
        </div>
      )}

      {/* Cart Sheet */}
      <CartSheet
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        cart={cart}
        onUpdateQuantity={updateQuantity}
        cartTotal={cartTotal}
        starsEarned={starsEarned}
      />

      {/* Customize Modal */}
      {customizing && (
        <CustomizeModal
          product={customizing}
          onClose={() => setCustomizing(null)}
          onAdd={addToCart}
        />
      )}
    </div>
  );
}